import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams, PaginatedAthleteListList } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseAthletes {
  searchTerm?: string;
  athleteStatus?: string;
  loginEnabled?: string;
  packages?: string[];
  ordering?: string;
  athleteStatusDate?: string;
  athleteStatusOnDate?: string;
  isActive?: string;
  hasPaymentMethod?: string;
  hasSignedWaiver?: string;
  glmLinkState?: string;
  gymId?: string;
  csvExport?: string;
  enabled?: boolean;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useAthletes({
  searchTerm,
  ordering,
  athleteStatus,
  loginEnabled,
  packages,
  athleteStatusDate,
  athleteStatusOnDate,
  isActive,
  hasPaymentMethod,
  hasSignedWaiver,
  glmLinkState,
  gymId,
  csvExport,
  limit = '20',
  enabled = true,
}: IUseAthletes): UseInfiniteQueryResult<AxiosResponse<PaginatedAthleteListList>> {
  const fetchAthletes = ({
    pageParam = { limit, offset: '0' },
  }: QueryKeyType): Promise<AxiosResponse<PaginatedAthleteListList>> => {
    const params = pageParam;

    if (searchTerm) {
      params.search = searchTerm;
    }
    if (ordering) {
      params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
    }
    if (athleteStatus) {
      params.athlete_status = athleteStatus;
    }
    if (loginEnabled) {
      params.profile__login_enabled = loginEnabled;
    }
    if (packages && packages.length > 0) {
      params.packages = packages.join(',');
    }
    if (athleteStatusDate) {
      params.athlete_status_date = athleteStatusDate;
    }

    if (athleteStatusOnDate) {
      params.athlete_status_on_date = athleteStatusOnDate;
    }

    if (isActive !== undefined) {
      params.is_active = isActive;
    }

    if (hasPaymentMethod) {
      params.has_payment_method = hasPaymentMethod;
    }

    if (hasSignedWaiver) {
      params.has_signed_waiver = hasSignedWaiver;
    }

    if (glmLinkState) {
      params.glm_link_state = glmLinkState;
    }

    if (gymId) {
      params.gym_id = gymId;
    }

    return client.get(`athletes/?${encodeUrlParams(params)}`);
  };

  return useInfiniteQuery(
    [
      'athletes',
      searchTerm,
      ordering,
      athleteStatus,
      loginEnabled,
      packages,
      athleteStatusDate,
      athleteStatusOnDate,
      isActive,
      hasPaymentMethod,
      hasSignedWaiver,
      glmLinkState,
      gymId,
      csvExport,
    ],
    fetchAthletes,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled,
    },
  );
}

export default useAthletes;
